import { InsuranceService } from './../../core/service/insurance.service';
import { InsurerService } from './../../core/service/insurer.service';
import { Component, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { merge, sortBy } from 'lodash';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import moment from 'moment';
import { Insurance, Insurer } from 'src/app/member/model/member';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { MemberService } from 'src/app/core/service/member.service';

@Component({
  selector: 'app-insurance-edit',
  templateUrl: './insurance-edit.component.html',
  styleUrls: ['./insurance-edit.component.scss']
})
export class InsuranceEditComponent implements OnInit
{
  @ViewChild('planControl') public planControl: any;
  @ViewChild('insurerControl') public insererControl: any;
  @ViewChild('fileInput') public fileInput: any;

  @Input() public currentMemberId: number;
  @Input() public isNew = false;

  @Output() public afterSave: EventEmitter<Insurance> = new EventEmitter();

  public editFormActive = false;
  public editForm: any;
  public insurers: any;
  public insurersFiltered: any;
  public defaultInsurerItem: Insurer = null;
  public currentDefaultInsuredItem: Insurer = null;
  public invalidFileExtensionText: string;
  public selectedCardFile: any = null;
  public isNewFileSelected = false;
  public isFileDeleted = false;
  public insurerUrlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  // File restrictions
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.jpg', '.jpe', '.jpeg', '.bmp', '.png', '.pdf'],
      maxFileSize: 2000
    };

  /**
 * Current search term entered from a comboxbox
 */
  public searchTerm: string;

  /**
   * Phone number input format mask
   */
  public phoneNumberMask: string;

  private requiredInsuranceFields = [];
  private insuranceFields: Array<{ text: string; value: string; key: string }>;

  private insurance: Insurance;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private insuranceService: InsuranceService,
    private translate: TranslateService,
    private settingService: SettingService,
    private messengerService: MessengerService,
    private insurerService: InsurerService,
    private memberService: MemberService
  )
  {
  }

  public get groupId(): UntypedFormControl
  {
    return this.editForm.get('groupId');
  }

  public get insuranceMemberId(): UntypedFormControl
  {
    return this.editForm.get('insuranceMemberId');
  }

  public get insurer(): UntypedFormControl
  {
    return this.editForm.get('insurer');
  }

  public get plan(): UntypedFormControl
  {
    return this.editForm.get('plan');
  }

  public get policyHolder(): UntypedFormControl
  {
    return this.editForm.get('policyHolder');
  }

  public get insuredFamilyName(): UntypedFormControl
  {
    return this.editForm.get('insuredFamilyName');
  }

  public get insuredGivenName(): UntypedFormControl
  {
    return this.editForm.get('insuredGivenName');
  }

  public get startDate(): UntypedFormControl
  {
    return this.editForm.get('startDate');
  }

  public get endDate(): UntypedFormControl
  {
    return this.editForm.get('endDate');
  }

  public get employer(): UntypedFormControl
  {
    return this.editForm.get('employer');
  }

  public get insurerUrl(): UntypedFormControl
  {
    return this.editForm.get('insurerUrl');
  }

  public get businessPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('businessPhoneNumber');
  }

  public get cardDocumentName(): UntypedFormControl
  {
    return this.editForm.get('cardDocumentName');
  }

  @Input() public set model(insurance: Insurance)
  {
    this.translate.get(['SELECT-INSURER', 'INVALID-FILE-TYPE']).subscribe({
      next: (res) =>
      {
        this.defaultInsurerItem = { name: res['SELECT-INSURER'], id: null, active: true };
        this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];
      }
    });
    this.initModel(insurance);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.requiredInsuranceFields = sessionSetting.requiredInsuranceFields ? sessionSetting.requiredInsuranceFields.split(',') : [];
        this.insuranceFields = this.memberService.insuranceFields;
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    if (this.startDate.value !== null && this.startDate.value !== null)
    {
      const d1 = moment(this.endDate.value);
      const d2 = moment(this.startDate.value);
      const diffMins = d1.diff(d2, 'minutes');
      if (diffMins <= 0)
      {
        this.messageHandlerService.displayError('DATE-RANGE-ERROR');
        return;
      }
    }

    const objectToPersist = merge(this.insurance, this.editForm.value);

    // Remove the insurer if the reset option is selected. In that case, id = null.
    if (objectToPersist.insurer && objectToPersist.insurer.id === null)
    {
      objectToPersist.insurer = null;
      objectToPersist.insuranceMemberId = null;
      objectToPersist.plan = null;
      objectToPersist.groupId = null;
      objectToPersist.policyHolder = null;
      objectToPersist.insuredFamilyName = null;
      objectToPersist.insuredGivenName = null;
      objectToPersist.insurerUrl = null;
      objectToPersist.businessPhoneNumber = null;
      objectToPersist.card = null;
      objectToPersist.deleteCard = true;
    }
    else
    {
      // Set insurance card
      if (this.isNewFileSelected)
      {
        const base64 = this.selectedCardFile.split('base64,')[1];
        objectToPersist.card = base64;
      }
      else if (this.isFileDeleted)
      {
        objectToPersist.deleteCard = true;
      }
      else
      {
        objectToPersist.card = null;
      }
    }

    // If no card or deleted, remove other properties
    if (!objectToPersist.card || objectToPersist.deleteCard)
    {
      delete objectToPersist.cardDocumentName;
      delete objectToPersist.cardDocumentMimeType;
    }

    this.insuranceService.saveInsurance(objectToPersist.id, objectToPersist, this.isNew, this.currentMemberId).subscribe({
      next: (data) =>
      {
        this.messageHandlerService.displaySuccess('INSURANCE-SAVED-SUCCESSFULLY');
        this.afterSave.emit(data);
        this.messengerService.sendMessage(new MessageSubject('refreshPatientHeader'));
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.editFormActive = false;
        document.body.style.overflow = '';
      }
    });
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Handle Insurer filtering.
   *
   * @param filter Filter entered.
   */
  public handleInsurerFilter(filter: string): void
  {
    this.searchTerm = filter;
    this.insurersFiltered = sortBy(this.insurers
      .filter((s: Insurer) => s.name.toLowerCase().includes(filter.toLowerCase()) && (s.active || !filter)), ['name']);

    // Setting currentDefaultInsuredItem is a workaround to hide the 'Select an insurer...' when insurer not found.
    this.currentDefaultInsuredItem = this.insurersFiltered.length === 0 ? null : this.defaultInsurerItem;
  }

  public insurerItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: Event): void
  {
    const input = event.target as HTMLInputElement;
    const fileList: FileList = input.files;
    if (fileList.length > 0)
    {
      const file: File = fileList[0];

      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }
      this.insurance.cardDocumentName = file.name;
      this.insurance.cardDocumentMimeType = file.type;
      this.cardDocumentName.setValue(file.name);
      this.editForm.markAsDirty();
      this.setFileFromBlob(file);
    }
  }

  public deleteInsuranceCard(): void
  {
    this.isFileDeleted = true;
    this.isNewFileSelected = false;
    this.cardDocumentName.setValue(null);
    this.fileInput.nativeElement.value = '';
    this.editForm.markAsDirty();
  }

  public openInsurerUrl(): void
  {
    if (this.isValidInsurerUrl())
    {
      window.open(this.insurerUrl.value);
    }
  }

  public isValidInsurerUrl(): boolean
  {
    const regEx = new RegExp(this.insurerUrlPattern);
    return this.insurerUrl.value !== undefined && this.insurerUrl.value && regEx.test(this.insurerUrl.value);
  }

  public isRequiredInsurance(insuranceField: string): boolean
  {
    return this.requiredInsuranceFields.indexOf(insuranceField) !== -1;
  }

  public requiredLabelInsurance(insuranceField: string): string
  {
    return this.isRequiredInsurance(insuranceField) ? '*' : '';
  }

  public requiredTextInsurance(insuranceField: string): string
  {
    const foundInsuranceField = this.insuranceFields.find(x => x.value === insuranceField);
    if (foundInsuranceField)
    {
      return foundInsuranceField.text;
    }
    else
    {
      return insuranceField;
    }
  }

  private getRequiredValidatorInsurance(insuranceField: string)
  {
    if (this.isRequiredInsurance(insuranceField))
    {
      return Validators.required;
    }
    else
    {
      return null;
    }
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  /**
   * Initialized model on open.
   *
   * @param insurance Insurance model to edit.
   */
  private initModel(insurance: Insurance)
  {
    if (insurance === undefined)
    {
      return;
    }

    this.insurerService.getInsurerList().subscribe({
      next: (data) =>
      {
        this.insurers = data;
        this.handleInsurerFilter('');

        if (this.isNew)
        {
          insurance.id = 0;
          insurance.insurer = null;
          insurance.groupId = '';
          insurance.insuranceMemberId = '';
          insurance.plan = '';
          insurance.policyHolder = '';
          insurance.insuredFamilyName = '';
          insurance.insuredGivenName = '';
          insurance.employer = '';
          insurance.insurerUrl = '';
          insurance.businessPhoneNumber = '';
          insurance.startDate = null;
          insurance.endDate = null;
          insurance.cardDocumentName = null;
        }
        else
        {
          if (insurance.cardDocumentName === undefined)
          {
            insurance.cardDocumentName = null;
          }

          if (insurance.startDate)
          {
            insurance.startDate = new Date(insurance.startDate);
          }

          if (insurance.endDate)
          {
            insurance.endDate = new Date(insurance.endDate);
          }
        }

        let urlValidatorsRequired = null;
        if (this.isRequiredInsurance('INSURER_WEBSITE'))
        {
          urlValidatorsRequired = [
            Validators.required,
            Validators.pattern(this.insurerUrlPattern)
          ];
        }
        else
        {
          urlValidatorsRequired = [Validators.pattern(this.insurerUrlPattern)];
        }

        // Set form
        this.editForm = this.fb.group({
          id: insurance.id,
          insurer: [{ value: insurance.insurer, disabled: false }, this.getRequiredValidatorInsurance('INSURER')],
          groupId: [{ value: insurance.groupId, disabled: false }, this.getRequiredValidatorInsurance('GROUP_ID')],
          insuranceMemberId: [{ value: insurance.insuranceMemberId, disabled: false }, this.getRequiredValidatorInsurance('INSURANCE_MEMBER_ID')],
          plan: [{ value: insurance.plan, disabled: false }, this.getRequiredValidatorInsurance('PLAN')],
          policyHolder: [{ value: insurance.policyHolder, disabled: false }, this.getRequiredValidatorInsurance('POLICY_HOLDER')],
          insuredFamilyName: [{ value: insurance.insuredFamilyName, disabled: false }, this.getRequiredValidatorInsurance('INSURED_LAST_NAME')],
          insuredGivenName: [{ value: insurance.insuredGivenName, disabled: false }, this.getRequiredValidatorInsurance('INSURED_FIRST_NAME')],
          employer: [{ value: insurance.employer, disabled: false }, this.getRequiredValidatorInsurance('EMPLOYER')],
          insurerUrl: [{ value: insurance.insurerUrl, disabled: false }, urlValidatorsRequired],
          businessPhoneNumber: [{ value: insurance.businessPhoneNumber, disabled: false }, this.getRequiredValidatorInsurance('INSURER_PHONE')],
          startDate: [{ value: insurance.startDate, disabled: false }, this.getRequiredValidatorInsurance('START_DATE')],
          endDate: [{ value: insurance.endDate, disabled: false }, this.getRequiredValidatorInsurance('END_DATE')],
          cardDocumentName: [{ value: insurance.cardDocumentName, disabled: false }, this.getRequiredValidatorInsurance('INSURANCE_CARD')],
        });

        this.insurance = insurance;
        this.editFormActive = true;
        document.body.style.overflow = 'hidden';
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.selectedCardFile = reader.result;
      this.isNewFileSelected = true;
      this.isFileDeleted = false;
    }, false);
    reader.readAsDataURL(file);
  }
}
